import { useState } from 'react';
import { getWidgetPreviewComponent } from './WidgetPreviews';
import { getPlatformsWithIcons } from '../../utils/platformIcons';
import { Icon } from '../../utils/icons';
import { formatPlatformNameForDisplay } from '../../utils/platformNames';
import RenameWidgetModal from './RenameWidgetModal';
import HeaderSettingsEmbedded from './HeaderSettingsEmbedded';
import StyleCustomization from './StyleCustomization';
import ColorPicker, { ColorPresets } from './ColorPicker';

// Font mapping helper function
const getFontDisplayName = (fontId) => {
    const fontMap = {
        'default': 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        'arial': 'Arial, sans-serif',
        'helvetica': 'Helvetica, Arial, sans-serif',
        'inter': 'Inter, system-ui, sans-serif',
        'roboto': 'Roboto, system-ui, sans-serif',
        'opensans': '"Open Sans", system-ui, sans-serif',
        'lato': 'Lato, system-ui, sans-serif',
        'montserrat': 'Montserrat, system-ui, sans-serif',
        'poppins': 'Poppins, system-ui, sans-serif',
        'nunito': 'Nunito, system-ui, sans-serif',
        'sourcesans': '"Source Sans Pro", system-ui, sans-serif',
        'ubuntu': 'Ubuntu, system-ui, sans-serif',
        'raleway': 'Raleway, system-ui, sans-serif',
        'dmsans': '"DM Sans", system-ui, sans-serif',
        'worksans': '"Work Sans", system-ui, sans-serif',
        'karla': 'Karla, system-ui, sans-serif',
        'oswald': 'Oswald, system-ui, sans-serif',
        'opensans-condensed': '"Open Sans Condensed", system-ui, sans-serif',
        'questrial': 'Questrial, system-ui, sans-serif',
        'playfair': '"Playfair Display", Georgia, serif',
        'merriweather': 'Merriweather, Georgia, serif',
        'lora': 'Lora, Georgia, serif',
        'crimson': '"Crimson Text", Georgia, serif',
        'fira': '"Fira Code", "Courier New", monospace'
    };
    return fontMap[fontId] || fontMap.default;
};

// Add custom styles for the range slider
const sliderStyles = `
.slider::-webkit-slider-thumb {
    appearance: none;
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.slider::-moz-range-thumb {
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}
`;

// Inject styles if not already present
if (typeof document !== 'undefined' && !document.getElementById('slider-styles')) {
    const style = document.createElement('style');
    style.id = 'slider-styles';
    style.textContent = sliderStyles;
    document.head.appendChild(style);
}

// Widget preview component
const WidgetPreview = ({ templateId, selectedPlatforms = [], previewReviews = [], loadingPreviewReviews = false, previewMode = 'desktop', externalPlatformData = [], locationData = [], widgetConfig = {} }) => {
    // All components now handle light/dark themes internally based on widgetConfig.style.colorScheme
    const PreviewComponent = getWidgetPreviewComponent(templateId);

    if (!PreviewComponent) {
        return (
            <div className="bg-gray-100 p-8 rounded-lg flex items-center justify-center">
                <p className="text-gray-500">Preview not available</p>
            </div>
        );
    }

    // Since we're now fetching reviews directly for selected platforms from the API,
    // we don't need to filter again on the frontend
    const filteredReviews = Array.isArray(previewReviews) ? previewReviews : [];

    // Pass the filtered reviews and responsive props to the preview component
    return (
        <div className={`w-full ${previewMode === 'mobile' ? 'scale-90 origin-top' : ''}`}>
            <div className="mx-auto" style={{ width: '100%' }}>
                <PreviewComponent
                    reviews={filteredReviews}
                    loading={loadingPreviewReviews}
                    previewMode={previewMode}
                    selectedPlatforms={selectedPlatforms}
                    externalPlatformData={externalPlatformData}
                    locationData={locationData}
                    layout={widgetConfig.selectedLayout || 'carousel'}
                    position={widgetConfig.badgePosition || 'bottom-right'}
                    widgetConfig={widgetConfig}
                />
            </div>
        </div>
    );
};

// Skeleton Loading Components
const LocationsSkeleton = () => (
    <div className="space-y-2 max-h-32 overflow-y-auto animate-pulse">
        {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center space-x-3 p-3 border border-gray-600 rounded-lg">
                <div className="w-5 h-5 bg-gray-600 rounded"></div>
                <div className="flex-1">
                    <div className="h-4 bg-gray-600 rounded w-32 mb-1"></div>
                    <div className="h-3 bg-gray-700 rounded w-24"></div>
                </div>
            </div>
        ))}
    </div>
);

const PlatformsSkeleton = () => (
    <div className="space-y-4 animate-pulse">
        <div>
            <div className="h-4 bg-gray-600 rounded w-48 mb-3"></div>
            <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="bg-gray-800 border border-gray-600 rounded-lg p-3 relative">
                        <div className="flex flex-col items-center text-center">
                            <div className="w-8 h-8 bg-gray-600 rounded mb-2"></div>
                            <div className="h-3 bg-gray-600 rounded w-12 mb-1"></div>
                            <div className="h-3 bg-gray-700 rounded w-8"></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const ConfigurationSkeleton = () => (
    <div className="space-y-6 animate-pulse">
        {/* Widget Name Skeleton */}
        <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
            <div className="h-4 bg-gray-600 rounded w-24 mb-2"></div>
            <div className="h-10 bg-gray-700 rounded w-full"></div>
        </div>

        {/* Locations Skeleton */}
        <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
            <div className="h-4 bg-gray-600 rounded w-32 mb-3"></div>
            <LocationsSkeleton />
        </div>

        {/* Platforms Skeleton */}
        <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
            <div className="h-4 bg-gray-600 rounded w-48 mb-3"></div>
            <PlatformsSkeleton />
        </div>

        {/* Settings Skeleton */}
        <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
            <div className="h-4 bg-gray-600 rounded w-28 mb-3"></div>
            <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center justify-between">
                        <div className="h-3 bg-gray-600 rounded w-32"></div>
                        <div className="w-12 h-6 bg-gray-700 rounded-full"></div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const Step2Configure = ({
    widgetConfig,
    _onInputChange,
    availableLocations,
    loadingLocations,
    onLocationToggle,
    _platforms,
    platformCounts,
    loadingPlatformCounts,
    onPlatformToggle,
    onLayoutSelect,
    onWidgetConfigChange,
    previewMode,
    setPreviewMode,
    previewReviews,
    loadingPreviewReviews,
    onBack,
    onCreateWidget,
    isLoading,
    onWidgetNameChange,
    isEditingMode = false,
    onClose
}) => {
    const [activeTab, setActiveTab] = useState('sources');
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [activeStyleElement, setActiveStyleElement] = useState(null);

    // Check if initial loading (all key data is loading)
    const isInitialLoading = loadingLocations && loadingPlatformCounts && !availableLocations?.length && !platformCounts?.length;

    // Overall page skeleton for initial load
    if (isInitialLoading) {
        return (
            <div className="min-h-screen bg-gray-50">
                {/* Header Skeleton */}
                <div className="bg-white border-b border-gray-200 px-6 py-4 animate-pulse">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="w-6 h-6 bg-gray-300 rounded"></div>
                            <div className="h-6 bg-gray-300 rounded w-48"></div>
                        </div>
                        <div className="w-6 h-6 bg-gray-300 rounded"></div>
                    </div>
                </div>

                <div className="p-6 max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Left Column Skeleton */}
                        <div className="space-y-6">
                            <ConfigurationSkeleton />
                        </div>

                        {/* Right Column - Preview Skeleton */}
                        <div className="space-y-6">
                            <div className="bg-white rounded-xl p-6 border animate-pulse">
                                <div className="h-5 bg-gray-300 rounded w-32 mb-4"></div>
                                <div className="aspect-video bg-gray-200 rounded-lg"></div>
                            </div>

                            {/* Action Buttons Skeleton */}
                            <div className="flex space-x-4">
                                <div className="h-12 bg-gray-300 rounded-lg flex-1"></div>
                                <div className="h-12 bg-gray-300 rounded-lg flex-1"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const tabs = [
        { id: 'sources', name: 'Sources', icon: 'location' },
        { id: 'layout', name: 'Layout', icon: 'grid' },
        { id: 'header', name: 'Header', icon: 'article' },
        { id: 'style', name: 'Style', icon: 'palette' }
    ];

    // Get platforms from selected location
    const getSelectedLocationPlatforms = () => {
        if (!widgetConfig.selectedLocations || widgetConfig.selectedLocations.length === 0 || !availableLocations) {
            return [];
        }

        // Get the selected location
        const selectedLocationId = widgetConfig.selectedLocations[0]; // Assuming single location selection
        const selectedLocation = availableLocations.find(location => location._id === selectedLocationId);

        if (!selectedLocation) {
            return [];
        }

        if (!selectedLocation.platforms || selectedLocation.platforms.length === 0) {
            return [];
        }

        // Map location platforms to the format expected by the UI
        return selectedLocation.platforms.map(platform => ({
            id: platform.name,
            name: platform.name,
            icon: platform.platform_logo,
            count: platform.total_reviews || 0,
            averageRating: platform.average_rating || 0,
            hasReviews: (platform.total_reviews || 0) > 0,
            uid: platform.uid,
            page_name: platform.page_name
        })).sort((a, b) => {
            // Sort by review count descending
            return b.count - a.count;
        });
    };

    // Create platform data with counts and icons (fallback for when no location selected)
    const getPlatformData = () => {
        // If location is selected, use location platforms
        const locationPlatforms = getSelectedLocationPlatforms();
        if (locationPlatforms.length > 0) {
            return locationPlatforms;
        }

        // Fallback to general platform counts (existing logic)
        const platformCountsMap = {};

        // Process platform counts data (fallback when no location selected)
        if (platformCounts && Array.isArray(platformCounts)) {
            platformCounts.forEach((item) => {
                const platformId = item._id;

                if (platformId) {
                    platformCountsMap[platformId] = {
                        count: item.count,
                        averageRating: item.averageRating
                    };
                }
            });
        }

        // Get platforms with icons
        const platformsWithIcons = getPlatformsWithIcons();

        // Map platforms with review counts and metadata
        const allPlatforms = (platformsWithIcons || []).map(platform => {
            const platformData = {
                ...platform,
                count: platformCountsMap[platform.id]?.count || 0,
                averageRating: platformCountsMap[platform.id]?.averageRating || 0,
                hasReviews: (platformCountsMap[platform.id]?.count || 0) > 0
            };

            return platformData;
        });

        // Sort platforms: those with reviews first, then by count
        return allPlatforms.sort((a, b) => {
            if (a.hasReviews && !b.hasReviews) return -1;
            if (!a.hasReviews && b.hasReviews) return 1;
            return b.count - a.count;
        });
    };

    const platformData = getPlatformData();

    // Separate popular platforms (those with reviews) and others
    const popularPlatforms = (platformData || []).filter(p => p.hasReviews).slice(0, 6);
    const morePlatforms = (platformData || []).filter(p => !p.hasReviews);

    // If no popular platforms with reviews, show top platforms by general popularity
    const displayPopularPlatforms = popularPlatforms.length > 0 ? popularPlatforms : [
        (platformData || []).find(p => p.id === 'google'),
        (platformData || []).find(p => p.id === 'facebook'),
        (platformData || []).find(p => p.id === 'yelp')
    ].filter(Boolean);

    const renderPlatformButton = (platform) => {
        const isSelected = widgetConfig.selectedPlatforms?.includes(platform.id);
        const hasReviews = platform.hasReviews;

        return (
            <button
                key={platform.id}
                onClick={() => onPlatformToggle(platform.id)}
                disabled={false} // Always allow platform selection
                className={`flex flex-col items-center p-3 rounded-lg transition-all duration-200 ${isSelected
                    ? 'bg-blue-600 text-white'
                    : hasReviews
                        ? 'bg-gray-800 hover:bg-gray-700 text-white'
                        : 'bg-gray-700 hover:bg-gray-600 text-gray-300' // Still selectable even without reviews
                    }`}
            >
                <div className="w-12 h-12 mb-2 flex items-center justify-center">
                    {platform.icon ? (
                        // Use platform logo from shared API if available
                        <img
                            src={platform.icon}
                            alt={`${platform.name} icon`}
                            className="w-10 h-10 object-contain"
                            onError={(e) => {
                                // Hide failed image and show fallback
                                e.target.style.display = 'none';
                                const fallback = e.target.parentElement.querySelector('.fallback-icon');
                                if (fallback) fallback.style.display = 'block';
                            }}
                        />
                    ) : null}
                    {/* <PlatformIcon
                        platform={platform.id}
                        className={`w-10 h-10 fallback-icon ${platform.icon ? 'hidden' : 'block'}`}
                        alt={`${platform.name} icon`}
                    /> */}
                </div>
                <span className="text-xs text-center mb-1 font-medium">
                    {formatPlatformNameForDisplay(platform.name)}
                </span>
                {hasReviews && (
                    <div className="flex flex-col items-center">
                        <span className="text-xs font-medium text-green-400">
                            {platform.count} reviews
                        </span>
                        {platform.averageRating > 0 && (
                            <div className="flex items-center mt-1">
                                <Icon name="star" className="text-xs text-yellow-400" size={12} />
                                <span className="text-xs ml-1">
                                    {platform.averageRating.toFixed(1)}
                                </span>
                            </div>
                        )}
                    </div>
                )}
                {!hasReviews && (
                    <span className="text-xs text-gray-500">No reviews</span>
                )}
            </button>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={onBack}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div className="hidden sm:block">
                            <button
                                onClick={() => setShowRenameModal(true)}
                                className="text-left hover:text-blue-600 transition-colors"
                            >
                                <h1 className="text-xl font-semibold text-gray-900 hover:text-blue-600">
                                    {widgetConfig.name || 'Untitled All-in-One Reviews'}
                                </h1>
                            </button>
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                                <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                                <span>Enhanced</span>
                                {widgetConfig.selectedPlatforms?.length > 0 && (
                                    <>
                                        <span>•</span>
                                        <span>{widgetConfig.selectedPlatforms.length} platform{widgetConfig.selectedPlatforms.length > 1 ? 's' : ''} selected</span>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Mobile title - make it clickable */}
                        <div className="sm:hidden">
                            <button
                                onClick={() => setShowRenameModal(true)}
                                className="text-left hover:text-blue-600 transition-colors"
                            >
                                <h1 className="text-lg font-semibold text-gray-900 hover:text-blue-600">
                                    {widgetConfig.name || 'Untitled All-in-One Reviews'}
                                </h1>
                            </button>
                        </div>

                        {/* Mobile menu button */}
                        <button
                            onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                            className="sm:hidden p-1 rounded-md text-gray-400 hover:text-gray-600"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                    </div>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={onCreateWidget}
                            disabled={isLoading || (widgetConfig.type !== 'whatsapp' && widgetConfig.type !== 'appointment' && widgetConfig.selectedPlatforms.length === 0)}
                            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                        >
                            {isLoading && (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            )}
                            <span>
                                {isLoading
                                    ? (isEditingMode ? 'Updating...' : 'Creating...')
                                    : (isEditingMode ? 'Update Widget' : 'Create Widget')
                                }
                            </span>
                        </button>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors hidden sm:block"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile sidebar overlay */}
            {showMobileSidebar && (
                <div className="fixed inset-0 z-50 sm:hidden">
                    <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setShowMobileSidebar(false)}></div>
                    <div className="relative flex flex-col w-full max-w-xs bg-gray-800 text-white shadow-xl">
                        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-700">
                            <h2 className="text-lg font-semibold">Configuration</h2>
                            <button
                                onClick={() => setShowMobileSidebar(false)}
                                className="text-gray-400 hover:text-white"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                            {/* Mobile tab navigation */}
                            <div className="space-y-2 mb-6">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => {
                                            setActiveTab(tab.id);
                                            setShowMobileSidebar(false);
                                        }}
                                        className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${activeTab === tab.id
                                            ? 'bg-blue-600 text-white'
                                            : 'hover:bg-gray-700 text-gray-300'
                                            }`}
                                    >
                                        <Icon name={tab.icon} size={20} />
                                        <span>{tab.name}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Mobile configuration content */}
                            <div className="space-y-4">
                                {/* Sources Tab Content */}
                                {activeTab === 'sources' && (
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-300 mb-3">Select Location</h4>
                                            {loadingLocations ? (
                                                <LocationsSkeleton />
                                            ) : (
                                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                                    {(availableLocations || []).map(location => (
                                                        <label
                                                            key={location._id}
                                                            className="flex items-center space-x-2 p-2 rounded-lg bg-gray-800 hover:bg-gray-700 cursor-pointer transition-colors"
                                                        >
                                                            <input
                                                                type="radio"
                                                                name="selectedLocationMobile"
                                                                checked={widgetConfig.selectedLocations?.includes(location._id)}
                                                                onChange={() => onLocationToggle(location._id)}
                                                                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500 focus:ring-2"
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-sm font-medium text-white truncate">
                                                                    {location.name}
                                                                </div>
                                                                <div className="text-xs text-gray-400 truncate">
                                                                    {location.displayAddress || `${location.address?.city || ''}, ${location.address?.state || ''}`.replace(/^,\s*|\s*,$/, '') || 'No address available'}
                                                                </div>
                                                            </div>
                                                        </label>
                                                    ))}
                                                    {(availableLocations || []).length === 0 && (
                                                        <div className="text-center py-4">
                                                            <p className="text-sm text-gray-400 mb-2">No locations found</p>
                                                            <p className="text-xs text-gray-500">Please create a location first in your dashboard</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Platform selection for mobile */}
                                        {widgetConfig.selectedLocations && widgetConfig.selectedLocations.length > 0 && (
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-300 mb-3">
                                                    Platforms with Reviews ({widgetConfig.selectedLocations.length} location{widgetConfig.selectedLocations.length > 1 ? 's' : ''})
                                                </h4>
                                                {loadingPlatformCounts ? (
                                                    <PlatformsSkeleton />
                                                ) : (
                                                    <div className="grid grid-cols-2 gap-2">
                                                        {displayPopularPlatforms.map(renderPlatformButton)}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Layout Tab Content */}
                                {activeTab === 'layout' && (
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-300 mb-3">Choose Layout</h4>
                                            <div className="grid grid-cols-2 gap-2">
                                                {[
                                                    { name: 'Carousel', id: 'carousel', icon: 'carousel', description: 'Horizontal' },
                                                    { name: 'Grid', id: 'grid', icon: 'grid', description: 'Structured' },
                                                    { name: 'List', id: 'list', icon: 'list', description: 'Vertical' },
                                                    { name: 'Masonry', id: 'masonry', icon: 'widgets', description: 'Pinterest' },
                                                    { name: 'Badge', id: 'badge', icon: 'badge', description: 'Compact' },
                                                    { name: 'Floating Badge', id: 'floating-badge', icon: 'notifications', description: 'Floating' },
                                                    { name: 'Slider', id: 'slider', icon: 'settings', description: 'Interactive' }
                                                ].map((layout) => (
                                                    <button
                                                        key={layout.id}
                                                        onClick={() => onLayoutSelect?.(layout.id)}
                                                        className={`p-2 border transition-all duration-200 ${widgetConfig.selectedLayout === layout.id
                                                            ? 'bg-blue-600 border-blue-500 text-white'
                                                            : 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
                                                            }`}
                                                    >
                                                        <div className="flex flex-col items-center space-y-1">
                                                            <Icon name={layout.icon} size={20} />
                                                            <div className="text-center">
                                                                <div className="text-xs font-medium">{layout.name}</div>
                                                                <div className="text-xs opacity-75">{layout.description}</div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Style Tab Content for Mobile */}
                                {activeTab === 'style' && (
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-300 mb-3">Color Scheme</h4>
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => {
                                                        const lightColors = {
                                                            colorScheme: 'light',
                                                            background: {
                                                                ...widgetConfig.style?.background,
                                                                color: '#FFFFFF'
                                                            },
                                                            widgetTitle: {
                                                                ...widgetConfig.style?.widgetTitle,
                                                                titleColor: '#000000',
                                                                captionColor: '#666666'
                                                            },
                                                            header: {
                                                                ...widgetConfig.style?.header,
                                                                backgroundColor: '#FFFFFF',
                                                                textColor: '#000000'
                                                            },
                                                            review: {
                                                                ...widgetConfig.style?.review,
                                                                backgroundColor: '#FFFFFF',
                                                                textColor: '#000000',
                                                                reviewerNameColor: '#000000',
                                                                dateColor: '#666666',
                                                                sourceIconBackgroundColor: '#FFFFFF'
                                                            }
                                                        };
                                                        onWidgetConfigChange({
                                                            ...widgetConfig,
                                                            style: { ...widgetConfig.style, ...lightColors }
                                                        });
                                                    }}
                                                    className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${widgetConfig.style?.colorScheme !== 'dark'
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                        }`}
                                                >
                                                    <span>☀️</span>
                                                    <span>Light</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const darkColors = {
                                                            colorScheme: 'dark',
                                                            background: {
                                                                ...widgetConfig.style?.background,
                                                                color: '#1F2937'
                                                            },
                                                            widgetTitle: {
                                                                ...widgetConfig.style?.widgetTitle,
                                                                titleColor: '#FFFFFF',
                                                                captionColor: '#D1D5DB'
                                                            },
                                                            header: {
                                                                ...widgetConfig.style?.header,
                                                                backgroundColor: '#1F2937',
                                                                textColor: '#FFFFFF'
                                                            },
                                                            review: {
                                                                ...widgetConfig.style?.review,
                                                                backgroundColor: '#374151',
                                                                textColor: '#FFFFFF',
                                                                reviewerNameColor: '#FFFFFF',
                                                                dateColor: '#D1D5DB',
                                                                sourceIconBackgroundColor: '#4B5563'
                                                            }
                                                        };
                                                        onWidgetConfigChange({
                                                            ...widgetConfig,
                                                            style: { ...widgetConfig.style, ...darkColors }
                                                        });
                                                    }}
                                                    className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${widgetConfig.style?.colorScheme === 'dark'
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                        }`}
                                                >
                                                    <span>🌙</span>
                                                    <span>Dark</span>
                                                </button>
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="text-sm font-medium text-gray-300 mb-3">Accent Color</h4>
                                            <div className="space-y-3">
                                                <ColorPicker
                                                    value={widgetConfig.style?.accentColor || '#4A90E2'}
                                                    onChange={(color) => onWidgetConfigChange({
                                                        ...widgetConfig,
                                                        style: { ...widgetConfig.style, accentColor: color }
                                                    })}
                                                    label=""
                                                    showHex={true}
                                                    size="default"
                                                />

                                                <ColorPresets
                                                    selectedColor={widgetConfig.style?.accentColor || '#4A90E2'}
                                                    onColorSelect={(color) => onWidgetConfigChange({
                                                        ...widgetConfig,
                                                        style: { ...widgetConfig.style, accentColor: color }
                                                    })}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="text-sm font-medium text-gray-300 mb-3">Font Family</h4>
                                            <select
                                                value={widgetConfig.style?.fontFamily || 'default'}
                                                onChange={(e) => onWidgetConfigChange({
                                                    ...widgetConfig,
                                                    style: { ...widgetConfig.style, fontFamily: e.target.value }
                                                })}
                                                className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                                                style={{ fontFamily: getFontDisplayName(widgetConfig.style?.fontFamily || 'default') }}
                                            >
                                                <optgroup label="System Fonts">
                                                    <option value="default">Default (System)</option>
                                                    <option value="arial">Arial</option>
                                                    <option value="helvetica">Helvetica</option>
                                                </optgroup>
                                                <optgroup label="Modern Sans-Serif">
                                                    <option value="inter">Inter</option>
                                                    <option value="roboto">Roboto</option>
                                                    <option value="opensans">Open Sans</option>
                                                    <option value="lato">Lato</option>
                                                    <option value="montserrat">Montserrat</option>
                                                    <option value="poppins">Poppins</option>
                                                    <option value="nunito">Nunito</option>
                                                    <option value="sourcesans">Source Sans Pro</option>
                                                    <option value="ubuntu">Ubuntu</option>
                                                    <option value="raleway">Raleway</option>
                                                    <option value="dmsans">DM Sans</option>
                                                    <option value="worksans">Work Sans</option>
                                                    <option value="karla">Karla</option>
                                                </optgroup>
                                                <optgroup label="Display & Condensed">
                                                    <option value="oswald">Oswald</option>
                                                    <option value="opensans-condensed">Open Sans Condensed</option>
                                                    <option value="questrial">Questrial</option>
                                                </optgroup>
                                                <optgroup label="Serif Fonts">
                                                    <option value="playfair">Playfair Display</option>
                                                    <option value="merriweather">Merriweather</option>
                                                    <option value="lora">Lora</option>
                                                    <option value="crimson">Crimson Text</option>
                                                </optgroup>
                                                <optgroup label="Monospace">
                                                    <option value="fira">Fira Code</option>
                                                </optgroup>
                                            </select>
                                            <p className="text-xs text-gray-500 mt-2">Font applies to all widget text</p>
                                        </div>
                                    </div>
                                )}

                                {/* Header Tab Content for Mobile */}
                                {activeTab === 'header' && (
                                    <div className="text-center py-8">
                                        <div className="text-4xl mb-2">📄</div>
                                        <h4 className="text-sm font-medium text-gray-300 mb-2">Header Settings</h4>
                                        <p className="text-xs text-gray-500">Available in desktop mode</p>
                                    </div>
                                )}

                                {/* Other tabs placeholder */}
                                {!['sources', 'layout', 'style', 'header'].includes(activeTab) && (
                                    <div className="text-center py-8">
                                        <div className="text-4xl mb-2">{tabs.find(t => t.id === activeTab)?.icon}</div>
                                        <h4 className="text-sm font-medium text-gray-300 mb-2">{tabs.find(t => t.id === activeTab)?.name} Settings</h4>
                                        <p className="text-xs text-gray-500">Configuration options coming soon</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex h-[calc(100vh-73px)]">
                {/* Left Sidebar - Icon Navigation - Hidden on mobile */}
                <div className="hidden sm:block w-20 bg-gray-800 text-white border-r border-gray-700">
                    <div className="flex flex-col items-center py-6 space-y-3">
                        {(tabs || []).map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-14 h-14 flex flex-col items-center justify-center transition-colors relative ${activeTab === tab.id
                                    ? 'bg-blue-600 text-white'
                                    : 'hover:bg-gray-700 text-gray-400'
                                    }`}
                                title={tab.name}
                            >
                                <Icon name={tab.icon} size={20} className="mb-1" />
                                <span className="text-xs">{tab.name}</span>
                                {activeTab === tab.id && (
                                    <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-blue-400 rounded-r"></div>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Right Sidebar - Content Panel - Responsive */}
                <div className="hidden sm:flex sm:w-80 bg-gray-900 text-white flex-col">
                    <div className="flex-1 overflow-y-auto">
                        {activeTab === 'sources' && (
                            <>
                                <div className="p-4">
                                    <h3 className="text-lg font-semibold mb-2">Add Review Sources</h3>
                                    <p className="text-sm text-gray-400 mb-4">
                                        First select locations, then choose platforms to display reviews from.
                                    </p>

                                    {/* Location Selection */}
                                    <div className="mb-6">
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="text-sm font-medium text-gray-300">Select Location</h4>
                                        </div>

                                        {loadingLocations ? (
                                            <LocationsSkeleton />
                                        ) : (
                                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                                {(availableLocations || []).map(location => (
                                                    <label
                                                        key={location._id}
                                                        className="flex items-center space-x-2 p-2 rounded-lg bg-gray-800 hover:bg-gray-700 cursor-pointer transition-colors"
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="selectedLocation"
                                                            checked={widgetConfig.selectedLocations?.includes(location._id)}
                                                            onChange={() => onLocationToggle(location._id)}
                                                            className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500 focus:ring-2"
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-sm font-medium text-white truncate">
                                                                {location.name}
                                                            </div>
                                                            <div className="text-xs text-gray-400 truncate">
                                                                {location.displayAddress || `${location.address?.city || ''}, ${location.address?.state || ''}`.replace(/^,\s*|\s*,$/, '') || 'No address available'}
                                                            </div>
                                                        </div>
                                                    </label>
                                                ))}
                                                {(availableLocations || []).length === 0 && (
                                                    <div className="text-center py-4">
                                                        <p className="text-sm text-gray-400 mb-2">No locations found</p>
                                                        <p className="text-xs text-gray-500">Please create a location first in your dashboard</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {                                        /* Selected location summary */}
                                        {widgetConfig.selectedLocations?.length > 0 && (
                                            <div className="mt-3 p-2 bg-blue-900/30 rounded-lg border border-blue-700">
                                                <p className="text-xs text-blue-300 mb-1">
                                                    {widgetConfig.selectedLocations.length} location selected
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Platform Search */}
                                    <div className="relative mb-6">
                                        <input
                                            type="text"
                                            placeholder="Search platforms..."
                                            className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg pl-10 border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                                        />
                                        <svg className="w-4 h-4 text-gray-400 absolute left-3 top-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>

                                    {/* Selected Platforms Summary */}
                                    {widgetConfig.selectedPlatforms?.length > 0 && (
                                        <div className="mb-4 p-3 bg-blue-900/30 rounded-lg border border-blue-700">
                                            <h4 className="text-sm font-medium text-blue-300 mb-2">Selected Platforms</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {(widgetConfig.selectedPlatforms || []).map(platformId => {
                                                    const platform = platformData.find(p => p.id === platformId);
                                                    return platform ? (
                                                        <div key={platformId} className="flex items-center space-x-1 bg-blue-600 text-white px-2 py-1 rounded text-xs">
                                                            <div className="w-3 h-3 flex items-center justify-center">
                                                                {platform.icon ? (
                                                                    <img
                                                                        src={platform.icon}
                                                                        alt={`${platform.name} icon`}
                                                                        className="w-3 h-3 object-contain"
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                            const fallback = e.target.parentElement.querySelector('.fallback-icon');
                                                                            if (fallback) fallback.style.display = 'block';
                                                                        }}
                                                                    />
                                                                ) : null}
                                                                {/* <PlatformIcon
                                                                    platform={platform.id}
                                                                    className={`w-3 h-3 fallback-icon ${platform.icon ? 'hidden' : 'block'}`}
                                                                /> */}
                                                            </div>
                                                            <span>{formatPlatformNameForDisplay(platform.name)}</span>
                                                            <span className="text-blue-200">({platform.count})</span>
                                                        </div>
                                                    ) : null;
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="px-4 pb-4 space-y-6">
                                    {/* Show message when no locations selected */}
                                    {(!widgetConfig.selectedLocations || widgetConfig.selectedLocations.length === 0) && (
                                        <div className="text-center py-8">
                                            <div className="text-gray-400 mb-4">
                                                <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                            </div>
                                            <p className="text-sm text-gray-400 mb-2">Select locations first</p>
                                            <p className="text-xs text-gray-500">
                                                Choose locations to see available review platforms
                                            </p>
                                        </div>
                                    )}

                                    {/* Show platform selection when locations are selected */}
                                    {widgetConfig.selectedLocations && widgetConfig.selectedLocations.length > 0 && (
                                        <>
                                            {/* Show loading state */}
                                            {loadingPlatformCounts && (
                                                <PlatformsSkeleton />
                                            )}

                                            {/* Platforms with Reviews */}
                                            {!loadingPlatformCounts && displayPopularPlatforms.length > 0 && (
                                                <div>
                                                    <h4 className="text-xs font-medium text-gray-400 mb-3 tracking-wider">
                                                        PLATFORMS WITH REVIEWS ({widgetConfig.selectedLocations.length} location{widgetConfig.selectedLocations.length > 1 ? 's' : ''})
                                                    </h4>
                                                    <div className="grid grid-cols-3 gap-3">
                                                        {displayPopularPlatforms.map(renderPlatformButton)}
                                                    </div>
                                                </div>
                                            )}

                                            {/* All Other Platforms */}
                                            {!loadingPlatformCounts && morePlatforms.length > 0 && (
                                                <div>
                                                    <h4 className="text-xs font-medium text-gray-400 mb-3 tracking-wider">
                                                        OTHER PLATFORMS
                                                    </h4>
                                                    <div className="grid grid-cols-3 gap-3">
                                                        {morePlatforms.map(renderPlatformButton)}
                                                    </div>
                                                </div>
                                            )}

                                            {/* No platforms message */}
                                            {!loadingPlatformCounts && (platformData || []).every(p => !p.hasReviews) && (
                                                <div className="text-center py-8">
                                                    <div className="text-gray-400 mb-4">
                                                        <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293L16 6.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                    </div>
                                                    <p className="text-sm text-gray-400 mb-2">No reviews found for selected locations</p>
                                                    <p className="text-xs text-gray-500">
                                                        Add reviews for the selected locations or choose different locations
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </>
                        )}

                        {activeTab === 'layout' && (
                            <div className="p-4 space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold mb-4">Choose Layout</h3>
                                    <div className="grid grid-cols-2 gap-3">
                                        {[
                                            { name: 'Carousel', id: 'carousel', icon: 'carousel', description: 'Horizontal scrolling' },
                                            { name: 'Grid', id: 'grid', icon: 'grid', description: 'Structured grid' },
                                            { name: 'List', id: 'list', icon: 'list', description: 'Vertical stacked' },
                                            { name: 'Masonry', id: 'masonry', icon: 'widgets', description: 'Pinterest-style' },
                                            { name: 'Badge', id: 'badge', icon: 'badge', description: 'Compact badges' },
                                            { name: 'Floating Badge', id: 'floating-badge', icon: 'notifications', description: 'Floating overlays' },
                                            { name: 'Slider', id: 'slider', icon: 'settings', description: 'Interactive slider' }
                                        ].map((layout) => (
                                            <button
                                                key={layout.id}
                                                onClick={() => onLayoutSelect?.(layout.id)}
                                                className={`p-3 border transition-all duration-200 ${widgetConfig.selectedLayout === layout.id
                                                    ? 'bg-blue-600 border-blue-500 text-white shadow-lg'
                                                    : 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
                                                    }`}
                                            >
                                                <div className="flex flex-col items-center space-y-2">
                                                    <Icon name={layout.icon} size={24} />
                                                    <div className="text-center">
                                                        <div className="text-sm font-medium">{layout.name}</div>
                                                        <div className="text-xs opacity-75">{layout.description}</div>
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Header Settings */}
                        {activeTab === 'header' && (
                            <div className="h-full">
                                <HeaderSettingsEmbedded
                                    widgetConfig={widgetConfig}
                                    onWidgetConfigChange={onWidgetConfigChange}
                                />
                            </div>
                        )}

                        {activeTab === 'style' && (
                            <>
                                {activeStyleElement ? (
                                    <StyleCustomization
                                        activeElement={activeStyleElement}
                                        onBack={() => setActiveStyleElement(null)}
                                        widgetConfig={widgetConfig}
                                        onWidgetConfigChange={onWidgetConfigChange}
                                    />
                                ) : (
                                    <div className="p-4">
                                        <h3 className="text-lg font-semibold mb-4">Style Settings</h3>

                                        {/* Color Scheme Section */}
                                        <div className="mb-6">
                                            <h4 className="text-sm font-medium text-gray-300 mb-3">Color Scheme</h4>
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => {
                                                        const lightColors = {
                                                            colorScheme: 'light',
                                                            background: {
                                                                ...widgetConfig.style?.background,
                                                                color: '#FFFFFF'
                                                            },
                                                            widgetTitle: {
                                                                ...widgetConfig.style?.widgetTitle,
                                                                titleColor: '#000000',
                                                                captionColor: '#666666'
                                                            },
                                                            header: {
                                                                ...widgetConfig.style?.header,
                                                                backgroundColor: '#FFFFFF',
                                                                textColor: '#000000'
                                                            },
                                                            review: {
                                                                ...widgetConfig.style?.review,
                                                                backgroundColor: '#FFFFFF',
                                                                textColor: '#000000',
                                                                reviewerNameColor: '#000000',
                                                                dateColor: '#666666',
                                                                sourceIconBackgroundColor: '#FFFFFF'
                                                            }
                                                        };
                                                        onWidgetConfigChange({
                                                            ...widgetConfig,
                                                            style: { ...widgetConfig.style, ...lightColors }
                                                        });
                                                    }}
                                                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${widgetConfig.style?.colorScheme !== 'dark'
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                        }`}
                                                >
                                                    <span>☀️</span>
                                                    <span>Light</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const darkColors = {
                                                            colorScheme: 'dark',
                                                            background: {
                                                                ...widgetConfig.style?.background,
                                                                color: '#1F2937'
                                                            },
                                                            widgetTitle: {
                                                                ...widgetConfig.style?.widgetTitle,
                                                                titleColor: '#FFFFFF',
                                                                captionColor: '#D1D5DB'
                                                            },
                                                            header: {
                                                                ...widgetConfig.style?.header,
                                                                backgroundColor: '#1F2937',
                                                                textColor: '#FFFFFF'
                                                            },
                                                            review: {
                                                                ...widgetConfig.style?.review,
                                                                backgroundColor: '#374151',
                                                                textColor: '#FFFFFF',
                                                                reviewerNameColor: '#FFFFFF',
                                                                dateColor: '#D1D5DB',
                                                                sourceIconBackgroundColor: '#4B5563'
                                                            }
                                                        };
                                                        onWidgetConfigChange({
                                                            ...widgetConfig,
                                                            style: { ...widgetConfig.style, ...darkColors }
                                                        });
                                                    }}
                                                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${widgetConfig.style?.colorScheme === 'dark'
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                        }`}
                                                >
                                                    <span>🌙</span>
                                                    <span>Dark</span>
                                                </button>
                                            </div>
                                            <div className="mt-2 text-xs text-gray-500">
                                                {widgetConfig.style?.colorScheme === 'dark'
                                                    ? 'Dark mode applies black/gray colors to widget elements'
                                                    : 'Light mode applies white/light colors to widget elements'
                                                }
                                            </div>
                                        </div>

                                        {/* Accent Color Section */}
                                        <div className="mb-6">
                                            <div className="flex items-center justify-between mb-3">
                                                <h4 className="text-sm font-medium text-gray-300">Accent Color</h4>
                                            </div>

                                            <div className="space-y-4">
                                                {/* Color Picker */}
                                                <ColorPicker
                                                    value={widgetConfig.style?.accentColor || '#4A90E2'}
                                                    onChange={(color) => onWidgetConfigChange({
                                                        ...widgetConfig,
                                                        style: { ...widgetConfig.style, accentColor: color }
                                                    })}
                                                    label=""
                                                    showHex={true}
                                                    size="large"
                                                />

                                                {/* Quick Color Presets */}
                                                <ColorPresets
                                                    selectedColor={widgetConfig.style?.accentColor || '#4A90E2'}
                                                    onColorSelect={(color) => onWidgetConfigChange({
                                                        ...widgetConfig,
                                                        style: { ...widgetConfig.style, accentColor: color }
                                                    })}
                                                />
                                            </div>
                                        </div>

                                        {/* Font Section */}
                                        <div className="mb-6">
                                            <h4 className="text-sm font-medium text-gray-300 mb-3">Font Family</h4>
                                            <div className="relative">
                                                <select
                                                    value={widgetConfig.style?.fontFamily || 'default'}
                                                    onChange={(e) => onWidgetConfigChange({
                                                        ...widgetConfig,
                                                        style: { ...widgetConfig.style, fontFamily: e.target.value }
                                                    })}
                                                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm appearance-none pr-10"
                                                    style={{ fontFamily: getFontDisplayName(widgetConfig.style?.fontFamily || 'default') }}
                                                >
                                                    <optgroup label="System Fonts">
                                                        <option value="default" style={{ fontFamily: 'system-ui' }}>Default (System)</option>
                                                        <option value="arial" style={{ fontFamily: 'Arial' }}>Arial</option>
                                                        <option value="helvetica" style={{ fontFamily: 'Helvetica' }}>Helvetica</option>
                                                    </optgroup>
                                                    <optgroup label="Modern Sans-Serif">
                                                        <option value="inter" style={{ fontFamily: 'Inter' }}>Inter</option>
                                                        <option value="roboto" style={{ fontFamily: 'Roboto' }}>Roboto</option>
                                                        <option value="opensans" style={{ fontFamily: 'Open Sans' }}>Open Sans</option>
                                                        <option value="lato" style={{ fontFamily: 'Lato' }}>Lato</option>
                                                        <option value="montserrat" style={{ fontFamily: 'Montserrat' }}>Montserrat</option>
                                                        <option value="poppins" style={{ fontFamily: 'Poppins' }}>Poppins</option>
                                                        <option value="nunito" style={{ fontFamily: 'Nunito' }}>Nunito</option>
                                                        <option value="sourcesans" style={{ fontFamily: 'Source Sans Pro' }}>Source Sans Pro</option>
                                                        <option value="ubuntu" style={{ fontFamily: 'Ubuntu' }}>Ubuntu</option>
                                                        <option value="raleway" style={{ fontFamily: 'Raleway' }}>Raleway</option>
                                                        <option value="dmsans" style={{ fontFamily: 'DM Sans' }}>DM Sans</option>
                                                        <option value="worksans" style={{ fontFamily: 'Work Sans' }}>Work Sans</option>
                                                        <option value="karla" style={{ fontFamily: 'Karla' }}>Karla</option>
                                                    </optgroup>
                                                    <optgroup label="Display & Condensed">
                                                        <option value="oswald" style={{ fontFamily: 'Oswald' }}>Oswald</option>
                                                        <option value="opensans-condensed" style={{ fontFamily: 'Open Sans Condensed' }}>Open Sans Condensed</option>
                                                        <option value="questrial" style={{ fontFamily: 'Questrial' }}>Questrial</option>
                                                    </optgroup>
                                                    <optgroup label="Serif Fonts">
                                                        <option value="playfair" style={{ fontFamily: 'Playfair Display' }}>Playfair Display</option>
                                                        <option value="merriweather" style={{ fontFamily: 'Merriweather' }}>Merriweather</option>
                                                        <option value="lora" style={{ fontFamily: 'Lora' }}>Lora</option>
                                                        <option value="crimson" style={{ fontFamily: 'Crimson Text' }}>Crimson Text</option>
                                                    </optgroup>
                                                    <optgroup label="Monospace">
                                                        <option value="fira" style={{ fontFamily: 'Fira Code' }}>Fira Code</option>
                                                    </optgroup>
                                                </select>
                                                <svg className="w-4 h-4 text-blue-500 absolute right-3 top-3 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-2">Font changes apply to all text in your widget preview and published widget.</p>
                                        </div>

                                        {/* Customize Elements Section */}
                                        <div className="mb-6">
                                            <h4 className="text-sm font-medium text-gray-300 mb-3">Customize Elements</h4>
                                            <div className="space-y-2">
                                                {[
                                                    { id: 'background', name: 'Background', icon: 'palette' },
                                                    { id: 'widgetTitle', name: 'Widget Title', icon: 'editNote' },
                                                    { id: 'rating', name: 'Rating', icon: 'star' },
                                                    { id: 'review', name: 'Review', icon: 'comment' },
                                                    { id: 'pagination', name: 'Pagination', icon: 'circle' }
                                                ].map((element) => (
                                                    <button
                                                        key={element.id}
                                                        onClick={() => setActiveStyleElement(element.id)}
                                                        className="w-full flex items-center justify-between p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                                                    >
                                                        <div className="flex items-center space-x-3">
                                                            <Icon name={element.icon} size={18} className="text-gray-300" />
                                                            <span className="text-sm text-white">{element.name}</span>
                                                        </div>
                                                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                        </svg>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>


                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Main Content - Widget Preview */}
                <div className="flex-1 bg-white p-4 sm:p-8 overflow-y-auto">
                    <div className="max-w-7xl mx-auto">
                        {/* Mobile Configuration Toggle */}
                        <div className="sm:hidden mb-6">
                            <button
                                onClick={() => setShowMobileSidebar(true)}
                                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg flex items-center justify-center space-x-2"
                            >
                                <span className="text-lg">{tabs.find(t => t.id === activeTab)?.icon}</span>
                                <span>Configure {tabs.find(t => t.id === activeTab)?.name}</span>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>

                        <div className="mb-6 sm:mb-8">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Widget Preview</h2>
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => setPreviewMode?.('desktop')}
                                        className={`px-3 py-2 rounded text-sm font-medium transition-colors ${previewMode === 'desktop'
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                    >
                                        <span className="hidden sm:inline">Desktop</span>
                                        <Icon name="computer" className="sm:block lg:hidden" size={20} />
                                    </button>
                                    <button
                                        onClick={() => setPreviewMode?.('mobile')}
                                        className={`px-3 py-2 rounded text-sm font-medium transition-colors ${previewMode === 'mobile'
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                    >
                                        <span className="hidden sm:inline">Mobile</span>
                                        <Icon name="mobile" className="sm:block lg:hidden" size={20} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Widget Preview */}
                        <div className={`transition-all duration-300 ${previewMode === 'mobile'
                            ? 'max-w-sm mx-auto'
                            : 'w-full'
                            }`}>
                            <div className={`${previewMode === 'mobile'
                                ? 'border-2 border-gray-300 rounded-[2rem] p-2 bg-gray-50 shadow-xl max-h-[600px] overflow-hidden'
                                : ''
                                }`}>
                                {widgetConfig.type ? (
                                    <div className={`w-full overflow-hidden ${previewMode === 'mobile' ? 'max-h-[580px] overflow-y-auto' : ''}`}>
                                        <WidgetPreview
                                            templateId={(() => {
                                                // Map the selected layout back to a template ID for proper component selection
                                                const layoutToTemplateMap = {
                                                    'carousel': 'carousel-widget',
                                                    'grid': 'grid',
                                                    'list': 'list',
                                                    'masonry': 'mansory',
                                                    'badge': 'badge',
                                                    'floating-badge': 'floating-badge',
                                                    'slider': 'slider'
                                                };

                                                // If user changed layout, use the new layout's template ID
                                                // Otherwise use the original template type
                                                const selectedLayout = widgetConfig.selectedLayout;
                                                if (selectedLayout && layoutToTemplateMap[selectedLayout]) {
                                                    // All components now handle dark themes internally based on widgetConfig.style.colorScheme
                                                    // No need to map to dark variants anymore

                                                    return layoutToTemplateMap[selectedLayout];
                                                }

                                                // Fallback to original template type
                                                return widgetConfig.type;
                                            })()}
                                            selectedPlatforms={widgetConfig.selectedPlatforms}
                                            previewReviews={previewReviews}
                                            loadingPreviewReviews={loadingPreviewReviews}
                                            previewMode={previewMode}
                                            externalPlatformData={platformData}
                                            locationData={availableLocations}
                                            widgetConfig={widgetConfig}
                                        />
                                    </div>
                                ) : (
                                    <div className={`bg-gray-100 p-8 rounded-lg flex items-center justify-center ${previewMode === 'mobile' ? 'min-h-[200px] max-h-[200px]' : 'min-h-[200px]'}`}>
                                        <div className="text-center">
                                            <Icon name="mobile" className="text-gray-400 mb-2" size={48} />
                                            <p className="text-gray-500 text-sm sm:text-base">Go back to Step 1 to select a template</p>
                                            <button
                                                onClick={onBack}
                                                className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                                            >
                                                ← Back to templates
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Platform selection status hint for both mobile and desktop */}
                        {widgetConfig.selectedPlatforms?.length === 0 ? (
                            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-start space-x-3">
                                    <div className="text-blue-500">
                                        <svg className="w-5 h-5 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-blue-800 text-sm">Using Sample Data</h3>
                                        <p className="text-blue-700 text-sm mt-1">
                                            <span className="sm:hidden">Tap &quot;Configure Sources&quot; above to</span>
                                            <span className="hidden sm:inline">Configure sources on the left to</span>
                                            {' '}select locations and platforms to show real review data in your widget preview.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-start space-x-3">
                                    <div className="text-green-500">
                                        <svg className="w-5 h-5 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-green-800 text-sm">Using Real Data</h3>
                                        <p className="text-green-700 text-sm mt-1">
                                            Showing reviews from {widgetConfig.selectedPlatforms.length} selected platform{widgetConfig.selectedPlatforms.length > 1 ? 's' : ''}: {widgetConfig.selectedPlatforms.join(', ')}.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Rename Widget Modal */}
            <RenameWidgetModal
                isOpen={showRenameModal}
                onClose={() => setShowRenameModal(false)}
                currentName={widgetConfig.name || 'Untitled All-in-One Reviews'}
                onSave={(newName) => {
                    if (onWidgetNameChange) {
                        onWidgetNameChange(newName);
                    }
                }}
            />
        </div>
    );
};

export default Step2Configure; 